import { round } from "lodash-es";

import type { QuestionDto } from "@/api/types";
import { HistogramChart } from "@/components/Charts/HistogramChart";
import { PieChart } from "@/components/Charts/PieChart";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { List } from "@/components/List/List";
import { User } from "@/components/User/User";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { avgBy } from "@/helpers/math";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { SurveyOpenQuestionDetailsModal } from "./SurveyOpenQuestionDetailsModal";

const MAX_RESPONSES_SHOWN = 5;

export function OpenAnswerWidget({ question }: { question: QuestionDto }): React.ReactNode {
  const [isDetailsModalOpened, detailsModalOpenHandlers] = useBool();

  const { t } = useTranslation();
  const responses = question.responses?.filter((response) => response.text);

  if (!responses) {
    return null;
  }

  const isAnyResponsesOmitted = responses.length > MAX_RESPONSES_SHOWN;

  return (
    <Stack gap="xl">
      <List getKey={(user) => user.id} items={responses.slice(0, MAX_RESPONSES_SHOWN)}>
        {(response) => {
          if (!response.text) {
            return null;
          }

          return (
            <Stack gap="sm" as="article">
              <User user={response.respondent} />
              <p>{response.text}</p>
            </Stack>
          );
        }}
      </List>
      {isAnyResponsesOmitted && (
        <footer className="flex w-full flex-col justify-center gap-space-sm @md:flex-row @md:items-center @md:justify-end">
          <Button variant="filled" onClick={detailsModalOpenHandlers.setTrue}>
            {t("page.survey-details.open-answers.show-more")}
          </Button>
          <SurveyOpenQuestionDetailsModal
            question={question}
            responses={responses}
            isOpened={isDetailsModalOpened}
            onOpenChange={detailsModalOpenHandlers.set}
          />
        </footer>
      )}
    </Stack>
  );
}

export function RatingWidget({ question }: { question: QuestionDto }): React.ReactNode {
  const { t } = useTranslation();
  if (!question.responses) {
    return null;
  }
  const validResponses = question.responses.filter((r) => r.rating != null);
  const averageRating = avgBy(
    validResponses,
    (r) => r.rating,
    () => 1,
  );

  return (
    <section className="mt-space-sm flex flex-col gap-space-sm">
      <Inline gap="sm" align="center" wrap={false}>
        <Star01Icon size={32} tone="rating" />
        <span className="text-heading-1">
          <span>{averageRating ? round(averageRating, 1) : "-"}</span>
          <span className="text-heading-3">/10</span>
        </span>
      </Inline>
      <span className="align-bottom text-text-secondary">
        {t("page.survey-details.rating.responses", {
          count: validResponses.length,
        })}
      </span>
    </section>
  );
}
export function MultipleChoiceWidget({ question }: { question: QuestionDto }): React.ReactNode {
  const { t } = useTranslation();
  if (!question.answers) {
    return null;
  }
  const total = question.responses?.filter((x) => x.offeredAnswerId).length ?? 0;
  const slices = question.answers.map((a) => ({
    x: a.text,
    y: question.responses?.filter((r) => r.offeredAnswerId === a.id)?.length ?? 0,
  }));
  if (question.answers?.length === 2) {
    return (
      <PieChart
        size={300}
        slices={slices.map((x) => ({
          amount: x.y,
          label: x.x,
        }))}
        hoverLabel={(x) => (
          <span className="flex max-w-select-responsive-filter flex-col">
            <strong>{x.label}</strong>
            <span>
              {t("page.survey-details.pie-chart.label.responses", {
                count: x.amount,
              })}
            </span>
            <span>
              {t("page.survey-details.pie-chart.label.percentage", {
                percentage: round((x.amount / total) * 100),
              })}
            </span>
          </span>
        )}
      />
    );
  }

  return (
    <Box padding={{ y: "sm" }}>
      <HistogramChart
        data={slices.map((x) => ({
          category: x.x,
          value: x.y,
        }))}
      />
    </Box>
  );
}
